import React from 'react';
import {
  SentimentSnapshot,
  SentimentVoteOption,
  SENTIMENT_OPTIONS
} from '../../types/sentiment';
import { cn } from '../../utils/cn';

interface SentimentBarProps {
  sentiment: SentimentSnapshot;
  highlightedOption?: SentimentVoteOption | null;
  isLoading?: boolean;
  showResults?: boolean;
}

const SENTIMENT_COLORS: Record<SentimentVoteOption, { bar: string; dot: string; text: string }> = {
  buy: {
    bar: 'bg-emerald-500',
    dot: 'bg-emerald-500',
    text: 'text-emerald-600 dark:text-emerald-300'
  },
  hold: {
    bar: 'bg-amber-400',
    dot: 'bg-amber-400',
    text: 'text-amber-600 dark:text-amber-300'
  },
  sell: {
    bar: 'bg-rose-500',
    dot: 'bg-rose-500',
    text: 'text-rose-500 dark:text-rose-300'
  }
};

export const SentimentBar: React.FC<SentimentBarProps> = ({
  sentiment,
  highlightedOption,
  isLoading = false,
  showResults = true
}) => {
  const totalVotes = sentiment.totalVotes;
  const isEmpty = totalVotes === 0;

  const segments = SENTIMENT_OPTIONS.map((option) => {
    const summary = sentiment[option];
    return {
      option,
      label: option.charAt(0).toUpperCase() + option.slice(1),
      percentage: summary.percentage,
      count: summary.count
    };
  });

  let offset = 0;

  return (
    <div className="space-y-3">
      <div
        className={cn(
          'relative h-3 w-full overflow-hidden rounded-full bg-muted',
          'transition-opacity duration-200',
          isLoading && 'opacity-60'
        )}
        aria-hidden
      >
        {showResults && !isEmpty
          ? segments.map(({ option, percentage }) => {
              const width = Math.max(percentage, 0);
              const segment = (
                <div
                  key={option}
                  className={cn(
                    'absolute top-0 h-full transition-all duration-500 ease-out',
                    SENTIMENT_COLORS[option].bar,
                    highlightedOption === option ? 'shadow-lg shadow-current/40' : 'opacity-80'
                  )}
                  style={{
                    left: `${offset}%`,
                    width: `${width}%`
                  }}
                />
              );

              offset += width;
              return segment;
            })
          : <div className="absolute inset-0 bg-muted-foreground/30" />}

        {/* Animated overlay for loading state */}
        {isLoading && (
          <div className="absolute inset-0 animate-pulse bg-muted/40" />
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 text-xs font-medium text-muted-foreground">
        {segments.map(({ option, label, percentage }) => (
          <div
            key={option}
            className={cn(
              'flex items-center gap-2 rounded-md px-2 py-1 transition-colors duration-200 ease-theme',
              highlightedOption === option && 'bg-muted'
            )}
          >
            <span
              className={cn(
                'h-2 w-2 rounded-full',
                showResults && !isEmpty ? SENTIMENT_COLORS[option].dot : 'bg-muted-foreground/40'
              )}
            />
            <span className="uppercase tracking-wide text-[11px]">{label}</span>
            {showResults && (
              <span className={cn('tabular-nums', isEmpty ? 'text-muted-foreground' : SENTIMENT_COLORS[option].text)}>
                {isEmpty ? '--' : `${percentage.toFixed(1)}%`}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SentimentBar;
